import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import KibDivider from '@chewy/kib-divider-react'
import { KibContainer } from '@chewy/kib-layout-react'
import { KibInlineAlert } from '@chewy/kib-messaging-react'

import {
  fetchPaymentPage,
  getUnappliedPaymentPageData,
} from '~/store/duck/clientBillingActivityData'
import { getUser } from '~/store/reducers/users'

import { CancelErrorBanner } from '../components/CancelErrorBanner'
import { CancelSuccessBanner } from '../components/CancelSuccessBanner'
import { ClientAccountCredit } from '../components/ClientAccountCredit'
import { ClientNotes } from '../components/ClientNotes'
import { InvoiceDetails } from '../components/InvoiceDetails'
import { PartialPaymentBanner } from '../components/PartialPaymentBanner'
import { PaymentErrorBanner } from '../components/PaymentErrorBanner'
import PaymentMethodSection from '../components/PaymentMethodSection'
import { usePaymentDialog } from '../context/PaymentDialogProvider'

import '@chewy/kib-divider-styles/dist/kib-divider.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-inline-alert.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import styles from './RecordPaymentScreen.module.scss'

interface RecordPaymentScreenProps {
  setIsFormValid: (isFormValid: boolean) => void
  setSendToTerminal: (sendToTerminal: boolean) => void
}

export const RecordPaymentScreen = ({
  setIsFormValid,
  setSendToTerminal,
}: RecordPaymentScreenProps) => {
  const {
    invoice,
    actions,
    dialogState,
    businessId,
    isEstimate,
    isFromAddPaymentFlow,
  } = usePaymentDialog()
  const dispatch = useDispatch()
  const unappliedPayments = useSelector(getUnappliedPaymentPageData)
  const client = useSelector(getUser(invoice?.clientId))
  const { t } = useTranslation(['Dialogs'])

  useEffect(() => {
    if (!isEstimate && client?.id) {
      dispatch(
        fetchPaymentPage({
          clientId: client.id,
          offset: 0,
          limit: 50,
          unapplied: true,
        }),
      )
    }
  }, [client?.id, isEstimate, dispatch])

  const handleAccountCreditChange = (data: {
    accountCreditTotal: number
    selectedAccountCreditIds: string[]
  }) => {
    actions.setPaymentData({
      ...dialogState.paymentData,
      selectedAccountCreditIds: data.selectedAccountCreditIds,
      accountCreditTotal: data.accountCreditTotal,
    })
  }

  const [remainingBalanceAfterCredits, setRemainingBalanceAfterCredits] =
    useState(invoice.dueToPay)
  const [shouldRecalculateCredits, setShouldRecalculateCredits] =
    useState(false)
  const [pendingRecalculation, setPendingRecalculation] = useState(false)

  useEffect(() => {
    // Update remainingBalance on successful partial payment.
    // We need both dependencies: when dueToPay changes OR when hasPartialPayment becomes true.
    // The condition inside ensures we only update when hasPartialPayment is true.
    if (dialogState.hasPartialPayment) {
      setRemainingBalanceAfterCredits(invoice.dueToPay)
      if (!isEstimate && client?.id) {
        dispatch(
          fetchPaymentPage({
            clientId: client.id,
            offset: 0,
            limit: 50,
            unapplied: true,
          }),
        )
        setPendingRecalculation(true)
      } else {
        setShouldRecalculateCredits(true)
      }
    }
  }, [
    invoice.dueToPay,
    dialogState.hasPartialPayment,
    isEstimate,
    client?.id,
    dispatch,
  ])

  useEffect(() => {
    if (
      pendingRecalculation &&
      unappliedPayments &&
      unappliedPayments.length > 0
    ) {
      setShouldRecalculateCredits(true)
      setPendingRecalculation(false)
    } else if (!unappliedPayments || unappliedPayments.length === 0) {
      setPendingRecalculation(false)
    }
  }, [unappliedPayments])

  return (
    <KibContainer className={styles.recordPaymentContainer}>
      {dialogState.hasPartialPayment && (
        <div className={styles.partialPaymentBanner}>
          <PartialPaymentBanner />
        </div>
      )}
      <div className={styles.invoiceSection}>
        <div className={styles.invoiceDetailsContainer}>
          <InvoiceDetails
            aggregatedPaymentAmount={invoice.paidAmount || 0}
            invoice={invoice}
            showAggregatedPayments={
              invoice.id.includes(',') || isFromAddPaymentFlow
            }
          />
          <ClientNotes clientId={invoice?.clientId} />
        </div>
        {isEstimate && (
          <KibInlineAlert
            className={styles.estimateAlert}
            severity="information"
          >
            {t('Dialogs:ADD_PAYMENT_DIALOG_V2.ESTIMATE_DEPOSIT_ALERT_TEXT')}
          </KibInlineAlert>
        )}
      </div>
      {!isEstimate && unappliedPayments?.length > 0 && (
        <div className={styles.clientAccountCreditSection}>
          <ClientAccountCredit
            businessId={businessId}
            remainingBalance={remainingBalanceAfterCredits}
            setRemainingBalance={setRemainingBalanceAfterCredits}
            setShouldRecalculate={setShouldRecalculateCredits}
            shouldRecalculate={shouldRecalculateCredits}
            unappliedPayments={unappliedPayments}
            onAccountCreditChange={handleAccountCreditChange}
          />
        </div>
      )}
      <KibDivider className={styles.sectionDivider} />
      <div className={styles.paymentMethodSection}>
        {dialogState.paymentError && (
          <PaymentErrorBanner
            message={dialogState.paymentError.message}
            severity={dialogState.paymentError.severity}
          />
        )}
        {dialogState.cancelError && !dialogState.paymentError && (
          <div className={styles.cancelErrorBanner}>
            <CancelErrorBanner message={dialogState.cancelError.message} />
          </div>
        )}
        {dialogState.cancelSuccessMessage && (
          <CancelSuccessBanner message={dialogState.cancelSuccessMessage} />
        )}
        <PaymentMethodSection
          remainingBalance={remainingBalanceAfterCredits}
          setIsFormValid={setIsFormValid}
          setSendToTerminal={setSendToTerminal}
        />
      </div>
    </KibContainer>
  )
}
